/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, ElementRef, forwardRef, Input, OnChanges, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import Handsontable from 'handsontable';
import * as moment from 'moment';

import cloneDeep from 'lodash-es/cloneDeep';
import {licenseKey} from '../hot-table/hot-table.directive';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TableInputComponent),
  multi: true
};

/**
 * An table type field which is using handsontable
 */
@Component({
  selector: 'table-input, table-input[ngModel]',
  template: `
    <div #container></div>`,
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  styleUrls: ['./table-input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TableInputComponent implements ControlValueAccessor, OnInit, OnChanges {
  public table: any;
  @Input()
  public readOnly: boolean;

  @Input()
  public startDate: number;

  @Input()
  public endDate: string;

  @Input()
  public typeOfItems: string[];

  @ViewChild('container', {static: true})
  public container: ElementRef;

  public options: { [key: string]: any };

  public grayCell = '#e0dfdf';

  // The internal data model
  private innerValue: any[] = [];

  // get accessor
  public get value(): any {
    if (!this.innerValue || this.innerValue.length === 0) {
      return;
    }

    return this.innerValue;
  }

  // set accessor including call the onchange callback
  public set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.propagateChange(v);
    }
  }

  constructor() {
    this.options = {
      licenseKey,
      // stretchH: 'all',
      autoWrapRow: true,
      height: 100,
      maxRows: 1,
      // width: 300,
      renderAllRows: false,
      columnSorting: false,
      sortIndicator: false,
      autoColumnSize: {
        samplingRatio: 15
      },
      manualRowResize: false,
      manualColumnResize: false,
      className: 'htCenter htMiddle',
      colWidths: 50,
      colHeaders: (index: number): string => {
        return moment(this.startDate || undefined)
          .add(index, 'days')
          .format(this.options?.data?.length && this.options.data[0]?.length > 7 ? 'dd <br/> D <br/> MMM' : 'dd');
      },
      cells: () => ({
        editor: !this.readOnly ? 'text' : false,
        type: 'numeric',
        renderer: (function (instance, td, row, col, prop, value, cellProperties) {
          Handsontable.renderers.TextRenderer.apply(this, arguments);
          if (value === 0) {
            td.style.backgroundColor = '#F9FFA3';

            if (this.typeOfItems && ['OFF', 'SUN', 'SAT'].includes(this.typeOfItems[col])) {
              td.innerHTML = '-';
              td.style.backgroundColor = this.grayCell;
            }
          }

          if (value === 25) {
            td.innerHTML = '-';
          }

          td.style.color = 'black';
          return td;
        }).bind(this)
      }),
      beforeKeyDown: function (event) {
        if (event.keyCode === 8 || event.keyCode === 46) {
          // BACKSPACE or DELETE was pressed
          return;
        }
        const workCycleRegex = /^((?:[0-9]|1[0-9]|2[0-3])(?:\.\d{0,2})?|24(?:\.00?)?)$/;
        const value = event.target.value ? (event.target.value + event.key).trim() : event.key;
        if (!workCycleRegex.test(value)) {
          event.preventDefault();
        }
      },
      afterChange: this.afterChange.bind(this),
      beforeChange: function (changes, source: any) {
        const [row, col, prevValue, newValue] = changes[0];
        if (newValue > 24 || newValue < 0) {
          this.setDataAtCell(row, col, prevValue, source);
        }
      }
    };
  }

  public propagateChange: any = () => {
  }

  // Set touched on blur
  public onBlur() {
  }

  // From ControlValueAccessor interface
  public writeValue(value: any) {
    if (value !== this.innerValue) {
      if (!Array.isArray(value)) {
        value = [];
      }

      this.innerValue = value;
      this.options.data = [this.innerValue];

      this.ngOnChanges();
    }
  }

  // From ControlValueAccessor interface
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  // From ControlValueAccessor interface
  public registerOnTouched(fn: any) {

  }

  public afterChange($event: any) {
    if ($event && $event[0]) {
      const [row, cell, prevValue, newValue] = $event[0];
      this.options.data[row][cell] = newValue === '-' ? 25 : +newValue || 0;
      for (let i = 0; i < cell; i++) {
        if (!this.options.data[row][i]) {
          this.options.data[row][i] = 0;
        }
      }
      this.value = this.options.data[0].slice();
      this.ngOnChanges();
    }
  }

  public ngOnInit() {
    this.table = new Handsontable(this.container.nativeElement, Object.assign(this.options, {data: this.innerValue}));
  }

  public ngOnChanges() {
    if (this.table) {
      const optionsData = cloneDeep(this.options.data);

      if (!this.startDate) {
        optionsData[0] = [];
      } else if (this.endDate) {
        const startDate = moment(this.startDate);
        const endDate = moment(this.endDate);
        const daysInBetween = !endDate.isBefore(startDate) ? endDate.diff(this.startDate, 'days') + 1 : 0;
        optionsData[0] = optionsData[0].slice(0, daysInBetween);
      }
      this.table.loadData(optionsData);
    }
  }
}
